import classNames from 'classnames';
import React, {useEffect, useMemo, useState} from 'react';
import {CSSTransition} from 'react-transition-group';
import {ACCOUNT_SECTION, ACCOUNT_SECTION_CONFIG} from '../../constants/account'
import {ANIMATION_TIMEOUT_APPEAR} from '../../constants/animation';
import ENDPOINT from '../../constants/endpoints';
import {Role} from '../../helpers/user';
import useApi from '../../hooks/useApi';
import AccountPageLayout from '../../layouts/AccountPageLayout';
import BasicDialog from '../../scenes/Dialog/components/BasicDialog';
import ResponseErrorDialog from '../../scenes/Dialog/components/ResponseErrorDialog';
import Form from '../../scenes/Form';
import AccountSettingsForm from '../../scenes/Form/components/AccountSettingsForm';
import Sidebar from '../../scenes/Sidebar';
import AccountSections from '../../scenes/Sidebar/components/AccountSections'
import './styles.css';

const Account = props => {
  const {
    user,
    history,
    globalOptions,
    pageTitle,
  } = props;
  const [isCandidate] = useMemo(() => [Role.isCandidate(user)], [user]);
  const [isGuest] = useMemo(() => [Role.isGuest(user)], [user]);
  const [isRecruiter] = useMemo(() => [Role.isRecruiter(user)], [user]);
  const [selected, setSelected] = useState(ACCOUNT_SECTION.PREFERENCES);
  const [subSection, setSubSection] = useState('');
  const api = useApi();
  const [nextEmail, setNextEmail] = useState();

  const sections = useMemo(() => {
    ACCOUNT_SECTION_CONFIG[ACCOUNT_SECTION.SUBSCRIPTION].visible = isCandidate && !isRecruiter;
    ACCOUNT_SECTION_CONFIG[ACCOUNT_SECTION.BILLING].visible = isCandidate && !isRecruiter;
    ACCOUNT_SECTION_CONFIG[ACCOUNT_SECTION.RECRUITER_NOTIFICATIONS].visible = isRecruiter;

    return ACCOUNT_SECTION_CONFIG;
  }, [isCandidate, isRecruiter]);

  useEffect(() => {
    const params = new URLSearchParams(history.location.search);
    const token = params.get('token') || null;
    const email = params.get('email') || null;
    const hashParts = (history.location.hash ? history.location.hash : '').replace(/^#/, '').split('/')
    const hash = hashParts.shift()
    const hashSubSection = hashParts.length > 0 ? hashParts.join('/') : '';
    setSubSection(hashSubSection)

    if (token && email) {
      api.get(
        ENDPOINT.ACTION.ACCOUNT.CHANGE_EMAIL_CONFIRM,
        { token, email },
        {
          handleError: response => ResponseErrorDialog({ response }),
          handleSuccess: () => {
            setNextEmail(email);
            BasicDialog({ bodyRender: <p>Your email has been changed</p> });
          },
          handleComplete: () => history.replace({ search: '' }),
        }
      );
    }

    const hasHashLink = [
        ACCOUNT_SECTION.PREFERENCES,
        ACCOUNT_SECTION.CANDIDATE_NOTIFICATIONS,
        ACCOUNT_SECTION.RECRUITER_NOTIFICATIONS,
        ACCOUNT_SECTION.PRIVACY,
        ACCOUNT_SECTION.SECURITY,
        ACCOUNT_SECTION.SUBSCRIPTION,
        ACCOUNT_SECTION.BILLING,
    ].includes(hash)
    if (hasHashLink) {
        setSelected(hash)
    }
  }, [history.location.hash]);

  const onChangeSectionHandler = section => {
    setSelected(section)
    window.location.hash = section;
  };

  let restrictedSections = [];

  if (isGuest) {
    restrictedSections = [
      ACCOUNT_SECTION.CANDIDATE_NOTIFICATIONS,
      ACCOUNT_SECTION.RECRUITER_NOTIFICATIONS,
    ]
  }

  if (isRecruiter && !isCandidate) {
    restrictedSections = [
      ACCOUNT_SECTION.CANDIDATE_NOTIFICATIONS,
      ACCOUNT_SECTION.PRIVACY,
    ]
  }

  if (!isRecruiter && isCandidate) {
    restrictedSections = [
      ACCOUNT_SECTION.RECRUITER_NOTIFICATIONS,
    ]
  }

  return (
    <AccountPageLayout
      pageClass={classNames('account-page', `account-page-${selected}`)}
      pageTitle={pageTitle}
      mainContent={
        <CSSTransition
          in={true}
          appear={true}
          timeout={ANIMATION_TIMEOUT_APPEAR}
          classNames="fade"
        >
          <Form>
            <AccountSettingsForm
              nextEmail={nextEmail}
              user={user}
              history={history}
              title="Account"
              sections={sections}
              activeSection={selected}
              activeSubSection={subSection}
              globalOptions={globalOptions}
            />
          </Form>
        </CSSTransition>
      }
      sidebarContent={
        <CSSTransition
          in={true}
          appear={true}
          timeout={ANIMATION_TIMEOUT_APPEAR}
          classNames="fade"
        >
          <Sidebar>
            <AccountSections
              restricted={true}
              onClick={onChangeSectionHandler}
              selected={selected}
              sections={Object.values(sections)}
              restrictedSections={restrictedSections}
            />
          </Sidebar>
        </CSSTransition>
      }
    />
  );
};

export default Account;
